import {
  useGetRoleOptions,
  useGetRolePermissions,
  useUpsertUser,
  usePermissions,
  useCompany,
} from '@dispatch/shared/hooks';
import { UserDetails, UserForm } from '@dispatch/web/features/users';
import { useNotificationMutationFactory } from '@dispatch/web/ui';
import { v4 as uuidv4 } from 'uuid';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreateUserPost,
  MultiSelectOption,
  RoleOptions,
  TaskGroup,
  UsersRole,
} from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';
import { useNotification } from '@dispatch/web/hooks';

export const UserCreatePage = () => {
  const { tenantId } = useCurrentUser();
  const { permissions } = usePermissions();
  const [options, setOptions] = React.useState<RoleOptions[]>();
  const navigate = useNavigate();
  const newUserId = uuidv4();
  const { notifySuccess } = useNotificationMutationFactory('User', 'create');
  const { notifyError } = useNotification();
  const onSuccess = (response: { success: boolean; message?: string }) => {
    if (response.success) {
      navigate(`/admin/users/${newUserId}`);
      notifySuccess();
    } else {
      notifyError('Error', response.message ?? 'User was not created');
    }
  };

  const { upsertUser } = useUpsertUser(newUserId, onSuccess);

  const { roles, taskGroups } = useCompany();
  const roleOptions = useGetRoleOptions(roles || []);

  React.useEffect(() => {
    setOptions(roleOptions);
  }, [roleOptions]);

  const taskGroupOptions = React.useMemo(() => {
    return taskGroups
      ?.filter((taskGroup: TaskGroup) => !!taskGroup.name)
      .map((taskGroup: TaskGroup) => {
        return { title: taskGroup.name, value: taskGroup.id };
      });
  }, [taskGroups]);

  const handleSave = (values: UserForm) => {
    const role: UsersRole = values?.role as UsersRole;
    const splitName: string[] = values.name.split(' ');
    const postBody: CreateUserPost = {
      id: newUserId,
      tenantId: tenantId || '' /* needs to not be required */,
      externalId: values?.externalId,
      name: values.name,
      firstName: splitName?.[0] || '',
      lastName: splitName?.[1] || '',
      email: values.email,
      disabledPermissions: values.disabledPermissions,
      roleId: role.value,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      deactivatedAt: null,
      resourceId: values?.resourceId
        ? String((values?.resourceId as MultiSelectOption).value)
        : null,
    };

    upsertUser(postBody);
  };
  const [usersRole, setUsersRole] = React.useState<UsersRole>();
  const [pullPermissions, setPullPermissions] = React.useState<boolean>(false);
  const { data: rolePermissions, status: getRolePermissionsStatus } =
    useGetRolePermissions(
      (usersRole?.data?.permissions as string[])?.map((p: string) => p) || [],
      pullPermissions
    );

  return (
    <UserDetails
      onSave={handleSave}
      taskGroupOptions={taskGroupOptions || []}
      roleOptions={options || []}
      rolePermissions={rolePermissions || []}
      rolePermissionStatus={getRolePermissionsStatus}
      usersRole={usersRole}
      setUsersRole={setUsersRole}
      pullPermissions={pullPermissions}
      setPullPermissions={setPullPermissions}
      permissions={permissions}
    />
  );
};
